/**
 * AI Tools Type Definitions
 *
 * Types for the ReAct pattern tool calling system that allows Marta
 * to interact with the database via GraphQL.
 */

/**
 * Simple JSON Schema type for tool parameters
 * (Simplified version without full JSON Schema dependency)
 */
export interface ToolParameterSchema {
  type: 'object' | 'string' | 'number' | 'boolean' | 'array';
  description?: string;
  properties?: Record<string, ToolPropertySchema>;
  required?: string[];
  items?: ToolPropertySchema;
  enum?: string[];
  default?: unknown;
}

export interface ToolPropertySchema {
  type?: string;
  description?: string;
  enum?: string[];
  default?: unknown;
  items?: ToolPropertySchema;
  properties?: Record<string, ToolPropertySchema>;
}

/**
 * Categories of tools available in the system
 */
export type ToolCategory =
  | 'projects'
  | 'tasks'
  | 'calendar'
  | 'contacts'
  | 'accounting'
  | 'hub'
  | 'support';

/**
 * CRUD operation types
 */
export type ToolOperation = 'create' | 'read' | 'update' | 'delete';

/**
 * Definition of a tool that can be called by the AI
 */
export interface ToolDefinition {
  /** Unique name of the tool */
  name: string;
  /** Human-readable description for the AI */
  description: string;
  /** Category for organization and routing */
  category: ToolCategory;
  /** Type of CRUD operation */
  operation: ToolOperation;
  /** JSON Schema defining the parameters */
  parameters: ToolParameterSchema;
  /** Whether this operation requires user confirmation */
  requiresConfirmation: boolean;
  /** GraphQL query or mutation string */
  graphqlOperation: string;
  /** Mapping function name for transforming parameters to GraphQL variables */
  mapParameters?: (params: Record<string, unknown>) => Record<string, unknown>;
  /** Mapping function for transforming GraphQL response */
  mapResponse?: (response: unknown) => unknown;
}

/**
 * A tool call extracted from the AI response
 */
export interface ToolCall {
  /** Optional reasoning/thought process from the AI */
  thought?: string;
  /** Name of the tool to execute */
  tool: string;
  /** Parameters for the tool call */
  parameters: Record<string, unknown>;
}

/**
 * Result of executing a tool call
 */
export interface ToolResult {
  /** Whether the tool execution was successful */
  success: boolean;
  /** Data returned from the tool (on success) */
  data?: unknown;
  /** Error message (on failure) */
  error?: string;
  /** Tool name for reference */
  tool?: string;
}

/**
 * A pending confirmation for destructive operations
 */
export interface PendingConfirmation {
  /** Name of the tool awaiting confirmation */
  tool: string;
  /** Parameters that will be used */
  parameters: Record<string, unknown>;
  /** Human-readable description of what will be done */
  description: string;
  /** Entity type being affected */
  entityType?: string;
  /** Entity ID being affected */
  entityId?: string;
  /** Entity name for display */
  entityName?: string;
}

/**
 * Status of a tool call in the message
 */
export type ToolCallStatus = 'pending' | 'executed' | 'failed' | 'awaiting_confirmation' | 'cancelled';

/**
 * Tool call information attached to a chat message
 */
export interface MessageToolCall {
  /** Tool name */
  tool: string;
  /** Parameters used */
  parameters: Record<string, unknown>;
  /** Current status */
  status: ToolCallStatus;
  /** Result data if executed */
  result?: unknown;
  /** Error message if failed */
  error?: string;
  /** Timestamp of execution */
  executedAt?: string;
}

/**
 * Schema for tools to be included in the system prompt
 */
export interface ToolSchema {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, unknown>;
    required?: string[];
  };
  requiresConfirmation: boolean;
}

/**
 * Options for tool execution
 */
export interface ToolExecutionOptions {
  /** Maximum number of tool calls per message (prevents infinite loops) */
  maxCalls?: number;
  /** Timeout in milliseconds for each tool call */
  timeout?: number;
  /** Account ID for scoped operations */
  accountId?: string;
  /** Whether to skip confirmation for delete operations (use with caution) */
  skipConfirmation?: boolean;
}

/**
 * Default execution options
 */
export const DEFAULT_EXECUTION_OPTIONS: Required<ToolExecutionOptions> = {
  maxCalls: 5,
  timeout: 30000,
  accountId: '',
  skipConfirmation: false,
};

/**
 * Patterns that indicate user confirmation for delete operations
 */
export const CONFIRMATION_PATTERNS = [
  /^s[ìi],?\s*(elimina|cancella|rimuovi)/i,
  /^confermo/i,
  /^procedi/i,
  /^ok,?\s*(elimina|cancella|rimuovi)/i,
  /^yes,?\s*(delete|remove)/i,
  /^confirm/i,
];

/**
 * Check if user input is a confirmation
 */
export function isConfirmation(input: string): boolean {
  const trimmed = input.trim().toLowerCase();
  return CONFIRMATION_PATTERNS.some(pattern => pattern.test(trimmed));
}

/**
 * Check if user input is a cancellation
 */
export function isCancellation(input: string): boolean {
  const trimmed = input.trim().toLowerCase();
  const cancelPatterns = [
    /^no/i,
    /^annulla/i,
    /^cancel/i,
    /^non\s+(eliminare|cancellare|rimuovere)/i,
    /^ferma/i,
    /^stop/i,
  ];
  return cancelPatterns.some(pattern => pattern.test(trimmed));
}
